import React, { useEffect, useState } from 'react';
import { useLocale } from '../i18n/LocaleContext';
import { buildCategoryOptions } from '../utils/eventCategories';
import { buildMusicGroupOptions, musicGroupIcon } from '../utils/musicGroups';
import { toggleInList } from '../utils/filters';
import { countEvents } from '../services/eventService';
import { CloseIcon } from './Icons';

function TextField({ label, value, options, placeholder, onChange, listId }) {
  return (
    <div className="field-group">
      <span className="field-group__label">{label}</span>
      <div className="field">
        <input
          type="text"
          list={listId}
          value={value || ''}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value || undefined)}
        />
        {value && (
          <button
            type="button"
            className="field__clear"
            onClick={() => onChange(undefined)}
            aria-label={`${label} ×`}
          >
            <CloseIcon size={14} />
          </button>
        )}
        <datalist id={listId}>
          {options.slice(0, 200).map((option) => (
            <option key={option.value} value={option.value} />
          ))}
        </datalist>
      </div>
    </div>
  );
}

function ChipGroup({ label, items, isSelected, onToggle }) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="field-group">
      <span className="field-group__label">{label}</span>
      <div className="chip-wrap">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`chip chip--sm${isSelected(item) ? ' chip--selected' : ''}`}
            onClick={() => onToggle(item)}
            aria-pressed={isSelected(item)}
          >
            {item.label}
            {item.count != null && <span className="chip__count">{item.count}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterDrawer({
  open,
  filters,
  filterOptions,
  searchQuery,
  onApply,
  onClear,
  onClose
}) {
  const { t } = useLocale();
  const [draft, setDraft] = useState(filters);
  const [previewCount, setPreviewCount] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(filters);
    }
  }, [open, filters]);

  // Live preview on the apply button, so nobody applies a filter set that
  // returns nothing and has to guess which chip caused it.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      countEvents({ ...draft, search: searchQuery || undefined })
        .then((count) => {
          if (!cancelled) {
            setPreviewCount(count);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPreviewCount(null);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, draft, searchQuery]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const patch = (changes) => setDraft((current) => ({ ...current, ...changes }));

  const categories = buildCategoryOptions(filterOptions.eventTypes).map((category) => ({
    key: category.id,
    id: category.id,
    label: `${category.icon} ${t(`cat.${category.id}`)}`,
    count: category.count
  }));

  const districts = filterOptions.districts.map((district) => ({
    key: String(district.value),
    id: district.value,
    label: t('district.label', { district: district.value }),
    count: district.count
  }));

  // Genres grouped under the three music groups; the group chip selects the
  // whole group, the genre chips below it narrow to single genres.
  const musicGroups = buildMusicGroupOptions(filterOptions.musicGroups);
  const selectedMusicGroups = draft.musicGroups || [];

  const features = filterOptions.specialFeatures.map((feature) => ({
    key: feature.value,
    id: feature.value,
    label: feature.value,
    count: feature.count
  }));

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={t('filters.title')}>
        <header className="drawer__head">
          <h2 className="drawer__title">{t('filters.title')}</h2>
          <button
            type="button"
            className="drawer__close"
            onClick={onClose}
            aria-label={t('filters.close')}
          >
            <CloseIcon size={18} />
          </button>
        </header>

        <div className="drawer__body">
          <ChipGroup
            label={t('filters.category')}
            items={categories}
            isSelected={(item) => draft.categories.includes(item.id)}
            onToggle={(item) => patch({ categories: toggleInList(draft.categories, item.id) })}
          />

          <ChipGroup
            label={t('filters.district')}
            items={districts}
            isSelected={(item) => draft.districts.includes(item.id)}
            onToggle={(item) => patch({ districts: toggleInList(draft.districts, item.id) })}
          />

          <div className="field-group">
            <span className="field-group__label">{t('filters.date')}</span>
            <div className="field-row">
              <div className="field">
                <input
                  type="date"
                  value={draft.dateFrom || ''}
                  aria-label={t('filters.dateFrom')}
                  onChange={(event) =>
                    patch({ dateFrom: event.target.value || undefined, quickDate: undefined })
                  }
                />
              </div>
              <div className="field">
                <input
                  type="date"
                  value={draft.dateTo || ''}
                  aria-label={t('filters.dateTo')}
                  min={draft.dateFrom || undefined}
                  onChange={(event) =>
                    patch({ dateTo: event.target.value || undefined, quickDate: undefined })
                  }
                />
              </div>
            </div>
          </div>

          <div className="field-group">
            <span className="field-group__label">{t('filters.price')}</span>
            <div className="chip-wrap">
              <button
                type="button"
                className={`chip chip--sm${draft.freeOnly ? ' chip--selected' : ''}`}
                onClick={() => patch({ freeOnly: !draft.freeOnly })}
                aria-pressed={draft.freeOnly}
              >
                {t('filters.freeOnly')}
                <span className="chip__count">{filterOptions.totals.free}</span>
              </button>
            </div>
          </div>

          <TextField
            label={t('filters.venue')}
            listId="venue-options"
            value={draft.venue}
            options={filterOptions.venues}
            placeholder={t('filters.venue')}
            onChange={(venue) => patch({ venue })}
          />

          {musicGroups.length > 0 && (
            <div className="field-group">
              <span className="field-group__label">{t('filters.music')}</span>
              {musicGroups.map((group) => {
                const groupSelected = selectedMusicGroups.includes(group.id);
                return (
                  <div key={group.id} className="genre-group">
                    <button
                      type="button"
                      className={`chip genre-group__chip${groupSelected ? ' chip--selected' : ''}`}
                      onClick={() =>
                        patch({ musicGroups: toggleInList(selectedMusicGroups, group.id) })
                      }
                      aria-pressed={groupSelected}
                    >
                      <span className="chip__icon">{musicGroupIcon(group.id)}</span>
                      {t(`music.${group.id}`)}
                      <span className="chip__count">{group.count}</span>
                    </button>
                    <div className="chip-wrap genre-group__genres">
                      {group.genres.map((genre) => {
                        const selected = draft.music_genre.includes(genre.value);
                        return (
                          <button
                            key={genre.value}
                            type="button"
                            className={`chip chip--sm${selected ? ' chip--selected' : ''}`}
                            onClick={() =>
                              patch({ music_genre: toggleInList(draft.music_genre, genre.value) })
                            }
                            aria-pressed={selected}
                          >
                            {genre.value}
                            <span className="chip__count">{genre.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <ChipGroup
            label={t('filters.features')}
            items={features}
            isSelected={(item) => draft.special_features.includes(item.id)}
            onToggle={(item) =>
              patch({ special_features: toggleInList(draft.special_features, item.id) })
            }
          />

          {filterOptions.cities.length > 1 && (
            <TextField
              label={t('filters.city')}
              listId="city-options"
              value={draft.city}
              options={filterOptions.cities}
              placeholder={t('filters.city')}
              onChange={(city) => patch({ city })}
            />
          )}
        </div>

        <footer className="drawer__foot">
          <button type="button" className="btn btn--ghost" onClick={onClear}>
            {t('filters.clear')}
          </button>
          <button
            type="button"
            className="btn btn--accent btn--block"
            onClick={() => onApply(draft)}
          >
            {previewCount == null
              ? t('filters.apply')
              : t('filters.applyCount', { count: previewCount })}
          </button>
        </footer>
      </aside>
    </>
  );
}
